import React from 'react';

export interface SegmentedOption {
  val: string;
  label: string;
  dot?: string;
}

interface ColorScheme {
  [key: string]: {
    bg: string;
    text: string;
    glow?: string;
  };
}

interface SegmentedControlProps {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  colorScheme?: ColorScheme;
}

const defaultColorScheme: ColorScheme = {
  online: {
    bg: '#16522a',
    text: 'text-green-400',
    glow: 'shadow-[0_0_6px_#22c55e88]',
  },
  offline: {
    bg: '#4a1515',
    text: 'text-red-400',
    glow: 'shadow-[0_0_6px_#ef444488]',
  },
  deleted: {
    bg: '#5a3a12',
    text: 'text-orange-400',
    glow: 'shadow-[0_0_6px_#f9731688]',
  },
};

// Used for any option whose val has no matching entry in colorScheme (e.g. a neutral "All" tab).
const neutralScheme = { bg: '#2a2a2b', text: 'text-white' };

const SegmentedControl: React.FC<SegmentedControlProps> = ({ options, value, onChange, colorScheme = defaultColorScheme }) => {
  const getColorScheme = (val: string) => {
    return colorScheme[val] || neutralScheme;
  };

  return (
    <div className="flex items-center gap-1 bg-[#1C1C1D] border border-[#333] rounded-[10px] p-1 flex-shrink-0 overflow-hidden">
      {/* Buttons — each sized to its own content (min/max clamped), not forced into equal slots */}
      {options.map(({ val, label, dot }) => {
        const scheme = getColorScheme(val);
        const isActive = value === val;

        return (
          <button
            key={val}
            onClick={() => onChange(val)}
            className={`flex items-center justify-center gap-1.5 px-4 py-1.5 min-h-[32px] max-h-[40px] max-w-[220px] rounded-[7px] text-[13px] font-medium whitespace-nowrap truncate transition-colors duration-200
              ${isActive ? scheme.text : 'text-[#888] hover:text-[#ccc]'}
            `}
            style={{ background: isActive ? scheme.bg : 'transparent' }}
          >
            {dot && (
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}
                  ${isActive && scheme.glow ? scheme.glow : ''}
                `}
              />
            )}
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
